<template>
  <div class="flex flex-col gap-y-4 text-primary">
    <div class="flex items-center gap-x-2 font-bold">
      <img :src="`${IMAGE_BASE_URL}/AI Summary Icon Container.png`" alt="" width="24" height="24" />
      AI Summary
      <span class="bg-point-color/20 text-point-color rounded-[32px] text-base px-1.5 py-0.5">BETA</span>
    </div>
      <div
        ref="responseContainerRef"
        class="response-container"
      >
      <span
        v-if="isInitialLoading"
        class="dot-test relative w-fit text-primary/40"
      >Generating response</span>
      <span
        v-else-if="isError"
        class="text-primary/40"
      >
        Failed to generate response. Please try again later.
      </span>
      <pre
        v-else
        v-html="markdownString"
        class="w-full whitespace-pre-line break-words text-lg"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import { marked } from 'marked'

import { aiQuestionType } from '~/types/ai-summary.type'
import { ITab } from '~/types/tab.type'
import { IMAGE_BASE_URL } from '~/constants/image.constant'
import BcTabs from '~/ui/Tabs/BcTabs.vue'

interface IProps {
  response: string
  isInitialLoading: boolean
  isError: boolean
  questionList: Array<ITab<aiQuestionType>>
  selectedQuestionType: aiQuestionType
  showTypingAnimation?: boolean
}

const props = defineProps<IProps>()
const emits = defineEmits([ 'update:selectedQuestionType' ])

const vmSelectedQuestionType = useVModel(props, 'selectedQuestionType', emits)
const { response, isError, questionList, showTypingAnimation } = toRefs(props)
const responseContainerRef = ref<HTMLElement | null>(null)
const markdownStringRef = ref<string>('')

const markdownString = computed(() => {
  return marked.parse(markdownStringRef.value)
})

// TODO: 이 인덱스 대신 for loop 사용할 수 있는지?
let index = 0
let timerId: NodeJS.Timeout | null

const initDisplay = () => {
  if (timerId) {
    clearTimeout(timerId)
    timerId = null
  }
  index = 0
}

const displayAiLikeResponse = () => {
  if (!response.value) {
    return
  }
  const words = response.value.split(' ')

  if (index >= words.length) {
    initDisplay()
    return
  }

  const randomTime = Math.floor(Math.random() * 100 + 50)
  const word = words[index++]
  markdownStringRef.value += word + ' '

  if (responseContainerRef.value) {
    responseContainerRef.value.scrollTop = responseContainerRef.value.scrollHeight
  }

  timerId = setTimeout(() => {
    displayAiLikeResponse()
  }, randomTime)
}

const handleDisplay = () => {
  markdownStringRef.value = ''
  initDisplay()
  if (showTypingAnimation.value) {
    displayAiLikeResponse()
  } else {
    markdownStringRef.value = response.value
  }
}

watch(() => [ showTypingAnimation.value, response.value ], handleDisplay, {
  immediate: true
})
</script>

<style scoped lang="scss">
.dot-test {
  &:after {
    @apply absolute bottom-0 left-full;
    content: '.';

    animation: test .8s linear infinite;
    @keyframes test {
      0% {
        content: '.';
      }
      33% {
        content: '..';
      }
      66% {
        content: '...';
      }
      100% {
        content: '.';
      }
    }
  }
}

.response-container {
  @apply p-3 h-40 overflow-y-auto bg-primary/5 border-primary/20 border rounded-lg;
  @include custom-scrollbar();
}
</style>
